import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Loader2, Plus, CheckCircle2 } from 'lucide-react';
import { fetchGlobalCategories, createGlobalCategory } from '@/lib/data-service';
import { saveRevisionSettings } from '@/lib/revision-settings-storage';
import type { BudgetRevisionSettings } from '@/types/resource-budget-settings.types';
import type { GlobalCategory } from '@/types/category.types';

interface CascadeTargetResolutionDialogProps {
  open: boolean;
  onResolved: (targetCategoryId: number, targetCategoryName: string) => void;
  onCancel: () => void;
  cascadeSettings: BudgetRevisionSettings;
}

type ResolutionPath = 'loading' | 'no-labour' | 'select-target' | 'confirm';

export function CascadeTargetResolutionDialog({
  open,
  onResolved,
  onCancel,
  cascadeSettings,
}: CascadeTargetResolutionDialogProps) {
  const [path, setPath] = useState<ResolutionPath>('loading');
  const [activeLabourCategories, setActiveLabourCategories] = useState<GlobalCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Path C state
  const [confirmedCategory, setConfirmedCategory] = useState<GlobalCategory | null>(null);

  // Path A: auto-generated code
  const [autoCode, setAutoCode] = useState('LAB-AUTO-1');

  const resolvePathOnOpen = useCallback(async () => {
    if (!open) return;
    setPath('loading');
    setError(null);
    setSelectedCategoryId('');
    setConfirmedCategory(null);

    try {
      const categories = await fetchGlobalCategories();
      const labourActive = categories.filter(
        (c) => c.type === 'labour' && c.status === 'active'
      );
      setActiveLabourCategories(labourActive);

      if (labourActive.length === 0) {
        // Path A: no labour categories
        const existingCodes = categories
          .map((c) => c.code ?? '')
          .filter((code) => code.startsWith('LAB-AUTO-'));
        let suffix = 1;
        while (existingCodes.includes(`LAB-AUTO-${suffix}`)) {
          suffix++;
        }
        setAutoCode(`LAB-AUTO-${suffix}`);
        setPath('no-labour');
        return;
      }

      const targetId = cascadeSettings.cascadeTargetCategoryId;
      const matchedTarget = targetId != null
        ? labourActive.find((c) => c.id === targetId)
        : null;

      if (matchedTarget) {
        // Path C: happy path
        setConfirmedCategory(matchedTarget);
        setPath('confirm');
      } else {
        // Path B: need to select
        setPath('select-target');
      }
    } catch (err) {
      console.error('[CascadeTargetResolutionDialog] Failed to fetch categories:', err);
      setError('Failed to load budget categories. Please try again.');
      setPath('no-labour');
    }
  }, [open, cascadeSettings.cascadeTargetCategoryId]);

  useEffect(() => {
    resolvePathOnOpen();
  }, [resolvePathOnOpen]);

  const persistTarget = async (categoryId: number, settings: BudgetRevisionSettings) => {
    await saveRevisionSettings({
      ...settings,
      cascadeTargetCategoryId: categoryId,
    });
  };

  // Path A: Create & Continue
  const handleCreateAndContinue = async () => {
    setIsProcessing(true);
    setError(null);
    let code = autoCode;
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      try {
        const newCategory = await createGlobalCategory({
          name: 'Labour',
          code,
          type: 'labour',
          description: 'Auto-created for Resource Budget cascade',
          isDefault: false,
        });

        await persistTarget(newCategory.id, cascadeSettings);
        setIsProcessing(false);
        onResolved(newCategory.id, newCategory.name);
        return;
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : String(err);
        if (errMsg.toLowerCase().includes('already exists')) {
          attempts++;
          const currentSuffix = parseInt(code.replace('LAB-AUTO-', ''), 10);
          code = `LAB-AUTO-${currentSuffix + 1}`;
          setAutoCode(code);
          continue;
        }
        setError(`Failed to create category: ${errMsg}`);
        setIsProcessing(false);
        return;
      }
    }
    setError('Failed to generate a unique category code after multiple attempts.');
    setIsProcessing(false);
  };

  // Path B: Set Target & Continue
  const handleSetTargetAndContinue = async () => {
    const id = parseInt(selectedCategoryId, 10);
    const cat = activeLabourCategories.find((c) => c.id === id);
    if (!cat) return;

    setIsProcessing(true);
    setError(null);
    try {
      await persistTarget(cat.id, cascadeSettings);
      setIsProcessing(false);
      onResolved(cat.id, cat.name);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(`Failed to save target setting: ${errMsg}`);
      setIsProcessing(false);
    }
  };

  // Path C: Continue
  const handleConfirmContinue = () => {
    if (confirmedCategory) {
      onResolved(confirmedCategory.id, confirmedCategory.name);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md">
        {path === 'loading' && (
          <>
            <DialogHeader>
              <DialogTitle>Resolving Cascade Target</DialogTitle>
              <DialogDescription>Checking available Labour categories...</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </>
        )}

        {path === 'no-labour' && (
          <>
            <DialogHeader>
              <DialogTitle>No Labour Category Found</DialogTitle>
              <DialogDescription>
                There are currently no Budget Categories with a Type of Labour. A system-generated
                category can be created for you to receive the cascaded budget values.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <div className="rounded-lg border p-3 bg-muted/30 space-y-1.5">
                <p className="text-sm font-medium">Category Preview</p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Labour</Badge>
                  <span className="text-sm font-medium">Labour</span>
                  <span className="text-xs text-muted-foreground">({autoCode})</span>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
                Cancel
              </Button>
              <Button onClick={handleCreateAndContinue} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create & Continue
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {path === 'select-target' && (
          <>
            <DialogHeader>
              <DialogTitle>Select Cascade Target</DialogTitle>
              <DialogDescription>
                There is currently no Budget Category set as the cascade target. Please select a
                Labour category to receive the cascaded budget values. This will also set it as the
                default target for future cascades.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a Labour category..." />
                </SelectTrigger>
                <SelectContent>
                  {activeLabourCategories.map((cat) => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.name} {cat.code ? `(${cat.code})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onCancel} disabled={isProcessing}>
                Cancel
              </Button>
              <Button
                onClick={handleSetTargetAndContinue}
                disabled={isProcessing || !selectedCategoryId}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Set Target & Continue'
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {path === 'confirm' && confirmedCategory && (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Cascade</DialogTitle>
              <DialogDescription>
                The Resource Budget will be cascaded to <strong>{confirmedCategory.name}</strong>
                {confirmedCategory.code ? ` (${confirmedCategory.code})` : ''}.
              </DialogDescription>
            </DialogHeader>

            <div className="py-2">
              <div className="rounded-lg border p-3 bg-muted/30">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success-600" />
                  <span className="text-sm font-medium">{confirmedCategory.name}</span>
                  {confirmedCategory.code && (
                    <Badge variant="outline" className="text-xs">{confirmedCategory.code}</Badge>
                  )}
                  <Badge variant="secondary" className="text-xs">Labour</Badge>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirmContinue}>
                Continue
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
